import React, { useState } from 'react';
import Button, { IconButton, SplitButton } from '@atlaskit/button/new';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/migration/chevron-down';
import { Box, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import type { IssueLinkType } from '@atlassian/jira-issue-shared-types/src/common/types/linked-issue-type.tsx';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIssueLinkTypesWithRelatesTo } from '../../utils/index.tsx';
import messages from './messages.tsx';

type FooterProps = {
	onButtonClick: (issueLinkType: IssueLinkType) => void;
	isButtonDisabled?: boolean;
	appearance?: 'default' | 'primary';
};

export const LinkSplitButton: React.FC<FooterProps> = ({
	onButtonClick,
	isButtonDisabled = false,
	appearance = 'default',
}) => {
	const { formatMessage } = useIntl();
	const { issueLinkTypes, relatesToIssueLinkType } = useIssueLinkTypesWithRelatesTo();
	const [isOpen, setIsOpen] = useState(false);

	if (relatesToIssueLinkType) {
		return (
			<SplitButton appearance={appearance} spacing="compact" isDisabled={isButtonDisabled}>
				<Button
					onClick={(e) => {
						e.stopPropagation();
						onButtonClick(relatesToIssueLinkType);
					}}
					isDisabled={isButtonDisabled}
					testId="ai-related-issues.ui.link-split-button.accept-all-loading-button"
				>
					{formatMessage(messages.linkAsRelatesTo)}
				</Button>
				<DropdownMenu<HTMLButtonElement>
					isOpen={isOpen}
					shouldRenderToParent
					trigger={({ triggerRef, ...triggerProps }) => (
						<IconButton
							ref={triggerRef}
							{...triggerProps}
							icon={ChevronDownIcon}
							label={formatMessage(messages.dropdownLabel)}
							isDisabled={isButtonDisabled}
						/>
					)}
					onOpenChange={(attrs: OnOpenChangeArgs) => {
						setIsOpen(attrs.isOpen);
					}}
					placement="right-end"
					shouldFlip
					zIndex={layers.spotlight}
				>
					<Box xcss={styles}>
						<DropdownItemGroup>
							{issueLinkTypes.map((issueLinkType) => (
								<DropdownItem
									onClick={(e) => {
										e.stopPropagation();
										onButtonClick(issueLinkType);
										setIsOpen(false);
									}}
									key={issueLinkType.id}
								>
									{issueLinkType.label}
								</DropdownItem>
							))}
						</DropdownItemGroup>
					</Box>
				</DropdownMenu>
			</SplitButton>
		);
	}

	return (
		<DropdownMenu<HTMLButtonElement>
			isOpen={isOpen}
			shouldRenderToParent
			trigger={({ triggerRef, ...triggerProps }) => (
				<Button iconAfter={ChevronDownIcon} ref={triggerRef} {...triggerProps}>
					{formatMessage(messages.linkAs)}
				</Button>
			)}
			onOpenChange={(attrs: OnOpenChangeArgs) => {
				setIsOpen(attrs.isOpen);
			}}
			placement="right-end"
			shouldFlip
			zIndex={layers.spotlight}
		>
			<Box xcss={styles}>
				<DropdownItemGroup>
					{issueLinkTypes.map((issueLinkType) => (
						<DropdownItem
							onClick={(e) => {
								e.stopPropagation();
								onButtonClick(issueLinkType);
								setIsOpen(false);
							}}
							key={issueLinkType.id}
						>
							{issueLinkType.label}
						</DropdownItem>
					))}
				</DropdownItemGroup>
			</Box>
		</DropdownMenu>
	);
};
const styles = xcss({ maxHeight: '300px', overflow: 'auto' });
